/** 
 Copyright Highway9 Networks Inc. 
 */ 
import { ReduxMiddleware } from "..";
import { socketRefreshHandler } from "../events/socketrefreshHander";
import { authenticationService } from "~/services";
import { utilityActions } from "../context/utilitySlice";
import evt from "../event";
import {
  connectEventsSocket,
  disconnectEventsSocket,
  initEventsSocketClient,
  sendEventsSocket,
  setEventsSocketFlushInterval,
} from "~/sw/eventWebsocket/eventsSocketClient";
import type { CoalescedFlushItem } from "~/sw/eventWebsocket/coalescedEventBatcher";

const env = window.location.protocol === 'https:' ? `wss://` :  `ws://`
const url = `${env}${window.location.host}/websocket`

/** Normal worker flush cadence (seconds). */
const FLUSH_INTERVAL_ACTIVE_S = 3;
/** Throttled cadence when the browser tab is hidden — reduces postMessage + main-thread overhead. */
const FLUSH_INTERVAL_HIDDEN_S = 30;

/**
 * Items received while the tab is hidden are held here.
 * They are drained once in a single idle callback when the tab becomes visible.
 * Beyond HIDDEN_QUEUE_MAX_PATCHES the queue is dropped and a full resync is
 * requested on refocus instead - a tab hidden for hours must not buffer
 * unbounded patch history (MCS-4316).
 */
const HIDDEN_QUEUE_MAX_PATCHES = 3000;
let hiddenFlushQueue: CoalescedFlushItem[] = [];
let hiddenQueuedPatches = 0;
let hiddenOverflow = false;
let drainCallbackId: number | null = null;

// requestIdleCallback is not available in Safari
const scheduleIdle = (cb: () => void): number =>
  typeof requestIdleCallback === "function"
    ? requestIdleCallback(cb, { timeout: 2000 })
    : window.setTimeout(cb, 200);
const cancelIdle = (id: number) => {
  if (typeof cancelIdleCallback === "function") {
    cancelIdleCallback(id);
  } else {
    window.clearTimeout(id);
  }
};

function dispatchFlushItems(items: CoalescedFlushItem[]) {
  for (const { objectType, patches } of items) {
    socketRefreshHandler(objectType, patches);
  }
}

function drainHiddenQueue() {
  if (drainCallbackId !== null) {
    cancelIdle(drainCallbackId);
    drainCallbackId = null;
  }
  if (hiddenOverflow) {
    // Replaying a truncated patch history would leave entities stale - re-pull instead
    hiddenOverflow = false;
    hiddenFlushQueue = [];
    hiddenQueuedPatches = 0;
    evt.emit("websocket:resync");
    return;
  }
  if (hiddenFlushQueue.length === 0) return;

  // Merge items by objectType so we dispatch one action per type, not one per buffered flush.
  const merged = new Map<string, CoalescedFlushItem>();
  for (const item of hiddenFlushQueue) {
    const existing = merged.get(item.objectType);
    if (existing) {
      existing.patches.push(...item.patches);
    } else {
      merged.set(item.objectType, { objectType: item.objectType, patches: [...item.patches] });
    }
  }
  hiddenFlushQueue = [];
  hiddenQueuedPatches = 0;
  dispatchFlushItems([...merged.values()]);
}

function onVisibilityChange() {
  if (document.hidden) {
    // Hold the buffer until the tab is actually visible again
    if (drainCallbackId !== null) {
      cancelIdle(drainCallbackId);
      drainCallbackId = null;
    }
    void setEventsSocketFlushInterval(FLUSH_INTERVAL_HIDDEN_S);
  } else {
    void setEventsSocketFlushInterval(FLUSH_INTERVAL_ACTIVE_S);
    // Drain buffered events in an idle callback so the tab-activation render is not blocked.
    drainCallbackId = scheduleIdle(drainHiddenQueue);
  }
}

let clientInitialized = false;

function ensureClient(api: Parameters<ReduxMiddleware>[0]) {
  if (clientInitialized) return;
  clientInitialized = true;

  document.addEventListener("visibilitychange", onVisibilityChange);

  initEventsSocketClient({
    onStatus: (status) => {
      if (status === "connected") {
        api.dispatch(utilityActions.setWebSocketStatus("connected"));
      } else {
        api.dispatch(utilityActions.setWebSocketStatus("reconnecting"));
      }
    },
    onFlush: (items) => {
      if (document.hidden) {
        // Tab is inactive: buffer items instead of doing Redux work on the main thread.
        if (hiddenOverflow) return;
        hiddenQueuedPatches += items.reduce((count, item) => count + item.patches.length, 0);
        if (hiddenQueuedPatches > HIDDEN_QUEUE_MAX_PATCHES) {
          hiddenFlushQueue = [];
          hiddenQueuedPatches = 0;
          hiddenOverflow = true;
          return;
        }
        hiddenFlushQueue.push(...items);
        return;
      }
      // A fresh flush must not overtake patches still buffered from the hidden
      // period - applying old merge patches after new ones would revert fields
      if (hiddenOverflow || hiddenFlushQueue.length > 0) {
        drainHiddenQueue();
      }
      // One worker flush may contain radios + edges + devices; route each to evt bus.
      dispatchFlushItems(items);
    },
  });
}

const webSocketMiddleWare : ReduxMiddleware = (api) => (next) => (action) => {
  switch (action.type) {
    case "socket/connect":
      ensureClient(api);
      {
        const token = authenticationService.currentUserValue!.token;
        void connectEventsSocket(url, token).catch((err) => {
          console.error("Events socket worker connect failed:", err);
        });
      }
      break;
    case "socket/disconnect":
      void disconnectEventsSocket();
      break;
    case "socket/send":
      void sendEventsSocket(action.payload);
      break;

    default:
      break;
  }
  return next(action);
}
export default webSocketMiddleWare;

export function connectSocket() {
  return { type: "socket/connect" } as const;
}
